import bbox from '@turf/bbox';
import bboxToPolygon from '@turf/bbox-polygon';
import { Geometry } from '@turf/helpers';
import scale from '@turf/transform-scale';
import React, { useCallback, useMemo } from 'react';
import { BoundingBox } from './BoundingBox';
import { LineString } from 'types/geojson';
import { hexToRgbArray } from 'kineticUtils/maps/hexToRgbArray';
import { MapToolbox } from 'components/maps/MapToolbox';
import BaseMap, { MapEntityClickEvent } from 'components/maps/BaseMap';

export const SegmentGeometryMap: React.FunctionComponent<{
  geometry?: LineString;
  onChange?: (geometry: LineString) => void;
  segmentColor?: string;
  segmentOpacity?: number;
  viewOnly?: boolean;
}> = ({ geometry, onChange, segmentColor, segmentOpacity, viewOnly = false }) => {
  const rgbaColor = useMemo(() => {
    return segmentColor ? hexToRgbArray(segmentColor) : undefined;
  }, [segmentColor]);

  // Get the bounding box of all points in the polygon to establish zoom level
  const mapBounds = useMemo(() => {
    if (!geometry) {
      return undefined;
    }
    const bounds = bbox(geometry);
    const polygon = bboxToPolygon(bounds);
    const scaled = scale(polygon, 1.2);
    return bbox(scaled);
  }, [geometry]);

  const handleLocationSelection = useCallback(
    (geometry: Geometry) => {
      onChange?.(geometry as LineString);
    },
    [onChange]
  );

  const toolbox = useMemo(
    () => (
      <MapToolbox
        geometry={geometry as Geometry}
        verticalPosition='top'
        horizontalPosition='right'
        onLocationSelection={handleLocationSelection}
        supportsLine={!viewOnly}
        supportsPoint={false}
        supportsArea={false}
        verticalPadding={60}
        geoJsonStyles={
          rgbaColor && segmentOpacity
            ? {
                fillColor: [...rgbaColor, Math.ceil(255 * segmentOpacity)],
                lineColor: [...rgbaColor, 255],
                lineWidth: 15,
              }
            : { lineWidth: 15 }
        }
      />
    ),
    [geometry, handleLocationSelection, segmentOpacity, rgbaColor, viewOnly]
  );

  return (
    <BaseMap
      contextChildren={[toolbox]}
      showMapMarkers
      mapEntityClickEvents={[MapEntityClickEvent.RIGHT]}
      disableClusterSelection={true}
    >
      <BoundingBox mapBounds={mapBounds} />
    </BaseMap>
  );
};

export default Map;
